import { useSearchParams } from 'react-router-dom';
import { useRef, useState } from 'react';
import Container from '@ui/container/container';
import { useAppSelector } from '@/store/hooks';
import { selectPasscodeLists } from '@/store/slices/passcode-lists/selectors';
import { AUDIENCES_VIEW_PATH, REGISTRANT_FILTER_TYPE } from '@/types/registration.types';
import AudiencesSearch from './audiences-search';
import AudiencesFilterByPasscodeLists from './audiences-filter-by-passcode-list';
import OptionalComponent from '@ui/optional-component/optional-component';
import { canEdit } from '../default-passcode-lists';
import AudiencesUploadButton from '../controls/audiences-upload-button';
import Button from '@ui/buttons/button/button';
import { ICONS } from '@ui/icons/types';
import useAudiencesLayoutType from '../hooks/use-audiences-layout-type';
import Icon from '@ui/icons/icon';
import Text from '@ui/text/text/text';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import Popover from '@ui/popover/popover';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import RotatableIcon from '@ui/RotatableIcon/rotatable-icon';
import RegistrantsStatusFilter from './registrants/registrants-status-filter';
import IconToggleV2 from '@ui/icon-toggle/icon-toggle-v2';

const getNumberArrayFromParams = (value: string) => {
  const numberArray: number[] = [];

  if (!value || !value.length) return numberArray;

  value.split(',').forEach((item) => {
    const num = parseInt(item.trim());
    if (!isNaN(num)) {
      numberArray.push(num);
    }
  });

  return numberArray;
};

type AudiencesFiltersProps = {
  expandedSearchWidth?: string;
  onAddClick?: () => void;
  onAddNewClick?: () => void;
  onUploadClick?: () => void;
  activeView?: string;
  status?: REGISTRANT_FILTER_TYPE | '';
};

const AudiencesFilters = ({
  expandedSearchWidth,
  onAddClick,
  onAddNewClick,
  onUploadClick,
  activeView,
  status,
}: AudiencesFiltersProps) => {
  const passcodeLists = useAppSelector(selectPasscodeLists);
  const popoverPortalRef = useRef<HTMLDivElement | null>(null);

  const [params, setParams] = useSearchParams();
  const paramsObj = Object.fromEntries(params.entries());
  const [filterPasscodeLists, setFilterPasscodeLists] = useState<number[]>(
    getNumberArrayFromParams(paramsObj.lists) || []
  );

  const [registrationStatusFilter, setRegistrationStatusFilter] = useState<string>(
    paramsObj.status || 'Sort by attendance'
  );

  const { handleViewChange, layout } = useAudiencesLayoutType();

  const handleSelectAllPasscodeLists = () => {
    const newLists = passcodeLists.every(
      (list) => !!list.passcode_list && filterPasscodeLists.includes(list?.passcode_list)
    )
      ? [] //all are selected,so deselect all
      : passcodeLists.map((list) => {
          return list.passcode_list || 0;
        }); //all are not selected, so select all

    setFilterPasscodeLists(newLists);
    setParams((prev) => {
      const currParams = Object.fromEntries(prev.entries());
      return {
        ...currParams,
        lists: newLists.join(','),
      };
    });
  };

  const handleSelectPasscodeList = (value: number) => {
    const newLists = filterPasscodeLists.includes(value)
      ? filterPasscodeLists.filter((passcodeList) => passcodeList !== value)
      : [...filterPasscodeLists, value];

    setFilterPasscodeLists(newLists);
    setParams((prev) => {
      const currParams = Object.fromEntries(prev.entries());
      return {
        ...currParams,
        lists: newLists.join(','),
      };
    });
  };

  const handleRegisteredStatusFilterChange = (filter: unknown) => {
    if (!filter || typeof filter !== 'object' || !('value' in (filter as object))) {
      return;
    }

    const { value } = filter as Record<string, string>;
    setRegistrationStatusFilter(value);

    setParams((prev) => {
      const currParams = Object.fromEntries(prev.entries());
      return {
        ...currParams,
        status: value,
      };
    });
  };

  return (
    <>
      <Container className="flex jc-space-between padding-b-5">
        <Container className="flex jc-flex-end gap-8">
          <AudiencesSearch expandedSearchWidth={expandedSearchWidth} />

          <OptionalComponent display={activeView === AUDIENCES_VIEW_PATH.REGISTRANTS}>
            <AudiencesFilterByPasscodeLists
              selectPasscodeList={handleSelectPasscodeList}
              selectAllPasscodeLists={handleSelectAllPasscodeLists}
              filterPasscodeLists={filterPasscodeLists}
            />
            <RegistrantsStatusFilter
              registrationStatusFilter={registrationStatusFilter}
              selectRegistrationStatusFilter={handleRegisteredStatusFilterChange}
            />
          </OptionalComponent>

          <OptionalComponent display={activeView !== AUDIENCES_VIEW_PATH.LISTS}>
            <AudiencesUploadButton
              onAddClick={onUploadClick}
              canEdit={activeView === AUDIENCES_VIEW_PATH.REGISTRANTS ? canEdit(status || '') : true}
            />
          </OptionalComponent>

          <OptionalComponent display={activeView === AUDIENCES_VIEW_PATH.LISTS}>
            <IconToggleV2
              leftIconName={ICONS.LAYOUT_GRID_4}
              leftValue="grid"
              rightIconName={ICONS.LAYOUT_FLIP_VERTICAL}
              rightValue="list"
              onToggle={handleViewChange}
              selected={layout}
              ariaLabel="Toggle audiences layout"
            />
          </OptionalComponent>

          <Popover
            closeOnClick
            width="180px"
            containerClassName="radius-12"
            cycleWithArrowKeys
            portalEl={popoverPortalRef}
            renderTriggerButton={({ open, closing }) => (
              <Button
                height="32px"
                padding="0 16px"
                clear
                className="flex ai-center jc-center gap-6"
                borderRadius="100px"
                backgroundColor={getThemeColorCSSVariable(EColorVariablesV2.primary)}
                fixedState={open ? 'hover' : undefined}
              >
                <Text
                  className="L2"
                  color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
                >
                  Add List
                </Text>
                <RotatableIcon
                  name={ICONS.KEYBOARD_ARROW_DOWN}
                  size={12}
                  rotate={open && !closing ? '180deg' : '0'}
                  color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
                />
              </Button>
            )}
          >
            {({ triggerBounds }) => {
              return (
                <Container
                  background={'var(--global-cont-3)'}
                  className="position-fixed z-20 overflow-y-auto"
                  borderRadius="12px"
                  height="64px"
                  width="130px"
                  style={{
                    top: triggerBounds?.top || 0,
                    left: (triggerBounds?.left || 0) + (triggerBounds?.width || 0),
                    transform: 'translate(-100%, 40px)',
                    minWidth: 'max-content',
                  }}
                >
                  <Button
                    className="flex fd-row jc-center ai-center gap-8 padding-h-16 padding-v-8"
                    onClick={onAddClick}
                  >
                    <Icon name={ICONS.ADD} size={12} color="var(--global-ico-text-2)" />
                    <Text className="P2" color="var(--global-ico-text-2)">
                      Existing List
                    </Text>
                  </Button>
                  <Button
                    className="flex fd-row jc-center ai-center gap-8 padding-h-16 padding-v-8"
                    onClick={onAddNewClick}
                  >
                    <Icon name={ICONS.ADD} size={12} color="var(--global-ico-text-2)" />
                    <Text className="P2" color="var(--global-ico-text-2)">
                      New List
                    </Text>
                  </Button>
                </Container>
              );
            }}
          </Popover>
        </Container>
      </Container>
    </>
  );
};

export default AudiencesFilters;
